import React, { useEffect, useRef, useState } from 'react'
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle, Transition } from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck, faSpinner, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import moment from 'moment'

const SUBMISSION_ERROR_MESSAGE = 'Sorry, something went wrong. Please try again.'

const InviteMeetingModal = ({ openInvite, setOpenInvite }) => {
  const axios = useAxiosPrivate()
  const [meetingName, setMeetingName] = useState('')
  const [meetingLocation, setMeetingLocation] = useState('')
  const [meetingDate, setMeetingDate] = useState('')
  const [meetingAttendees, setMeetingAttendees] = useState('')
  const [meetingPlace, setMeetingPlace] = useState('None Selected')
  const [loading, setLoading] = useState(false)
  const [showThanks, setShowThanks] = useState(false)
  const [submissionError, setSubmissionError] = useState('')
  const closeTimer = useRef(null)

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])

  const handleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = null
    setShowThanks(false)
    setSubmissionError('')
    setOpenInvite(false)
  }

  const resetInputs = () => {
    setMeetingName('')
    setMeetingLocation('')
    setMeetingDate('')
    setMeetingPlace('None Selected')
    setMeetingAttendees('')
  }

  const submitForm = async () => {
    if (loading) return

    setSubmissionError('')
    setLoading(true)

    try {
      const params = {
        meeting_name: meetingName,
        meeting_location: meetingLocation,
        meeting_attendees: meetingAttendees,
        meeting_date: moment(meetingDate).format('DD/MM/YYYY HH:mm'),
        meeting_place: meetingPlace,
        school_name: window?.Policy?.orgName || 'Unknown School',
      }

      const response = await axios.post('v1/staff-hub/invite/send', params)

      if (response.data?.error === true || response.data?.success === false) {
        throw new Error(response.data?.message || SUBMISSION_ERROR_MESSAGE)
      }

      setShowThanks(true)
      resetInputs()

      closeTimer.current = setTimeout(() => {
        setShowThanks(false)
        setOpenInvite(false)
        closeTimer.current = null
      }, 2000)
    } catch (error) {
      console.error('Failed to send meeting invite', error)
      setSubmissionError(SUBMISSION_ERROR_MESSAGE)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Transition show={openInvite} as={React.Fragment} >
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-30" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-xl rounded-lg shadow-xl bg-white overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between mb-4 p-5 border-b border-gray-200">
              <DialogTitle className="text-lg font-semibold text-gray-800 text-center w-full">
                Invite us to your meeting
              </DialogTitle>
              <button type="button" aria-label="Close meeting invitation form" className="text-gray-500 hover:text-gray-700" onClick={handleClose}>
                <FontAwesomeIcon icon={faXmarkCircle} className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              {!showThanks && (
                <>
                  <p className="text-center text-gray-700">
                    We would love to speak at your meetings! If you can invite us to an upcoming trust, cluster or federation meeting,
                    we’ll sponsor the event up to <strong>£200</strong>!
                  </p>
                  <p className="text-center font-semibold text-gray-800">
                    Please enter the details of the meeting below.
                  </p>

                  {submissionError && (
                    <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {submissionError}
                    </div>
                  )}

                  <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); submitForm() }}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Meeting Name</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                        value={meetingName}
                        onChange={(e) => setMeetingName(e.target.value)}
                        disabled={loading}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Meeting Location</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                        value={meetingLocation}
                        onChange={(e) => setMeetingLocation(e.target.value)}
                        disabled={loading}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Meeting Date</label>
                      <input
                        type="datetime-local"
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                        value={meetingDate}
                        onChange={(e) => setMeetingDate(e.target.value)}
                        disabled={loading}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Number of Attendees</label>
                      <input
                        type="number"
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                        value={meetingAttendees}
                        onChange={(e) => setMeetingAttendees(e.target.value)}
                        disabled={loading}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Meeting Place (On-site / Digital)</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                        value={meetingPlace}
                        onChange={(e) => setMeetingPlace(e.target.value)}
                        disabled={loading}
                        required
                      >
                        <option value="None Selected">Select an option</option>
                        <option value="on-site">On-site</option>
                        <option value="digital">Digital</option>
                      </select>
                    </div>

                    <div className="text-center">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-sas hover:bg-sas-dark text-white font-semibold py-2 px-4 rounded shadow disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {loading ? (
                          <span className="inline-flex items-center gap-2">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Sending Invite...
                          </span>
                        ) : 'Send Invite'}
                      </button>
                    </div>
                  </form>
                </>
              )}

              {showThanks && (
                <div role="status" aria-live="polite" className="text-center py-6">
                  <FontAwesomeIcon icon={faCircleCheck} className="mb-4 text-5xl text-green-600" />
                  <p className="text-lg font-semibold">Thank you for your invite!</p>
                  <p className="mt-2 text-sm text-gray-600">This window will close automatically.</p>
                </div>
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </Transition>
  )
}

export default InviteMeetingModal
